import { useAnimals, useBreeds, useCategories, useCreateAnimal, useLotMembershipHistory, useLots, type AnimalSortColumn, type AnimalSummary, type LotMembershipHistoryItem } from '@/api/hooks';
import { AnimalFloatingView } from '@/components/animals/AnimalFloatingView';
import { AnimalAvatar } from '@/components/animals/AnimalAvatar';
import { AnimalRowActionsMenu } from '@/components/animals/AnimalRowActionsMenu';
import { AssignLotModal } from '@/components/animals/AssignLotModal';
import { DeleteAnimalModal } from '@/components/animals/DeleteAnimalModal';
import { EventFormModal, REPRODUCTION_EVENT_TYPES } from '@/components/events/EventFormModal';
import { MovementFormModal } from '@/components/movements/MovementFormModal';
import { getErrorMessage } from '@/lib/api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { ClickableTableRow } from '@/components/ui/ClickableTableRow';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { DateInput } from '@/components/ui/DateInput';
import { Modal } from '@/components/ui/Modal';
import { PageHeader } from '@/components/ui/PageHeader';
import { ListPagination } from '@/components/ui/ListPagination';
import { SortableTableHeader } from '@/components/ui/SortableTableHeader';
import { Select } from '@/components/ui/Select';
import { Alert } from '@/components/ui/Alert';
import { useAuth } from '@/contexts/AuthContext';
import { usePropertyScope } from '@/contexts/PropertyContext';
import { formatDate, formatWeight, sexLabel, sexSymbol, breedLabel } from '@/lib/format';
import { cn } from '@/lib/cn';
import { canManage } from '@/lib/rbac';
import { Import, Plus, Search, Tags } from 'lucide-react';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

export function AnimalsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const lotFilter = Number(searchParams.get('lote')) || undefined;
  const inLotContext = Boolean(lotFilter);
  const { propertyId } = usePropertyScope();
  const { data: lots } = useLots(propertyId);
  const activeLot = lots?.lots.find((l) => l.id === lotFilter);
  const showImport = user && canManage(user.role);
  const canDelete = user && canManage(user.role);
  const [sexFilter, setSexFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [listTab, setListTab] = useState<'active' | 'history'>('active');
  const [sort, setSort] = useState<{ column: AnimalSortColumn; dir: 'asc' | 'desc' }>({
    column: 'internal_number',
    dir: 'asc',
  });
  const [page, setPage] = useState(1);

  useEffect(() => {
    setListTab('active');
    setPage(1);
  }, [lotFilter]);

  const showLotHistory = inLotContext && listTab === 'history';

  const { data, isLoading: animalsLoading } = useAnimals({
    property_id: propertyId ?? undefined,
    lot_id: lotFilter,
    sex: sexFilter || undefined,
    search: search || undefined,
    sort: sort.column,
    sort_dir: sort.dir,
    page: showLotHistory ? 1 : page,
    only_archive: !inLotContext && listTab === 'history',
  });

  const { data: lotHistoryData, isLoading: lotHistoryLoading } = useLotMembershipHistory(
    showLotHistory ? lotFilter : undefined,
    {
      search: search || undefined,
      sex: sexFilter || undefined,
    },
  );

  const isLoading = showLotHistory ? lotHistoryLoading : animalsLoading;

  const { data: presentCountData } = useAnimals({
    property_id: propertyId ?? undefined,
    lot_id: inLotContext ? lotFilter : undefined,
    page: 1,
    per_page: 1,
  });
  const { data: archiveCountData } = useAnimals({
    property_id: propertyId ?? undefined,
    page: 1,
    per_page: 1,
    only_archive: true,
  });
  const { data: lotHistoryCountData } = useLotMembershipHistory(
    inLotContext ? lotFilter : undefined,
    {},
  );

  const activeTabCount = inLotContext
    ? presentCountData?.meta.total
    : presentCountData?.meta.total;
  const historyTabCount = inLotContext
    ? lotHistoryCountData?.meta.total
    : archiveCountData?.meta.total;
  const [open, setOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AnimalSummary | null>(null);
  const [actionAnimal, setActionAnimal] = useState<AnimalSummary | null>(null);
  const [activeModal, setActiveModal] = useState<
    'event' | 'movement' | 'reproduction' | 'assignLot' | null
  >(null);
  const [openViews, setOpenViews] = useState<AnimalSummary[]>([]);
  const [focusedViewId, setFocusedViewId] = useState<number | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [menuOpenAnimalId, setMenuOpenAnimalId] = useState<number | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [propertyId, lotFilter, sexFilter, search, sort.column, sort.dir, listTab]);

  const hasActiveFilters = Boolean(search || sexFilter || (lotFilter && !inLotContext));
  const showEmptyCadastro =
    !isLoading &&
    propertyId &&
    listTab === 'active' &&
    !inLotContext &&
    (data?.meta.total ?? 0) === 0 &&
    !hasActiveFilters;
  const lotHistoryRows = lotHistoryData?.memberships ?? [];
  const listTotal = showLotHistory ? (lotHistoryData?.meta.total ?? 0) : (data?.meta.total ?? 0);

  function handleSort(column: AnimalSortColumn) {
    setSort((prev) =>
      prev.column === column
        ? { column, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { column, dir: 'asc' },
    );
  }

  useEffect(() => {
    const message = (location.state as { message?: string } | null)?.message;
    if (!message) return;
    setFlash(message);
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: null },
    );
  }, [location, navigate]);

  useEffect(() => {
    if (!flash) return;
    const timer = window.setTimeout(() => setFlash(null), 5000);
    return () => window.clearTimeout(timer);
  }, [flash]);

  const openAnimalView = useCallback((animal: AnimalSummary) => {
    setOpenViews((prev) => {
      if (prev.some((v) => v.id === animal.id)) return prev;
      return [...prev, animal];
    });
    setFocusedViewId(animal.id);
  }, []);

  const closeAnimalView = useCallback((animalId: number) => {
    setOpenViews((prev) => prev.filter((v) => v.id !== animalId));
    setFocusedViewId((prev) => (prev === animalId ? null : prev));
  }, []);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape' || focusedViewId == null) return;
      closeAnimalView(focusedViewId);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [focusedViewId, closeAnimalView]);

  function closeActionModal() {
    setActiveModal(null);
    setActionAnimal(null);
  }

  return (
    <div>
      {flash && (
        <Alert variant="success" className="mb-4">
          {flash}
        </Alert>
      )}

      <PageHeader
        title="Animais"
        subtitle={
          activeLot
            ? listTab === 'history'
              ? `Histórico de passagem pelo lote ${activeLot.name}`
              : `Animais presentes no lote ${activeLot.name}`
            : listTab === 'history'
              ? 'Animais arquivados (vendidos, abatidos ou só registro genealógico)'
              : 'Rebanho operacional da fazenda selecionada'
        }
        actions={
          <div className="flex flex-wrap gap-2">
            {activeLot && (
              <Button variant="outline" onClick={() => navigate('/lotes')}>
                Ver lotes
              </Button>
            )}
            {showImport && (
              <Button
                variant="outline"
                onClick={() => navigate('/importacao')}
                disabled={!propertyId}
              >
                <Import className="h-4 w-4" />
                Importar
              </Button>
            )}
            <Button onClick={() => setOpen(true)} disabled={!propertyId || listTab === 'history'}>
              <Plus className="h-4 w-4" />
              Novo animal
            </Button>
          </div>
        }
      />

      {propertyId && (
        <nav className="mb-4 flex flex-wrap gap-1 rounded-2xl border border-manejai-sand bg-manejai-cream/40 p-1">
          {(
            [
              {
                id: 'active' as const,
                label: inLotContext ? 'Presentes no lote' : 'Ativos',
                count: activeTabCount,
              },
              {
                id: 'history' as const,
                label: 'Histórico',
                count: historyTabCount,
              },
            ]
          ).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setListTab(tab.id)}
              className={cn(
                'rounded-xl px-3 py-2 text-sm font-medium transition-colors',
                listTab === tab.id
                  ? 'bg-white text-manejai-forest shadow-sm'
                  : 'text-manejai-bark/70 hover:text-manejai-forest',
              )}
            >
              {tab.label}
              {typeof tab.count === 'number' ? (
                <span className="ml-1.5 text-xs text-manejai-bark/50">{tab.count}</span>
              ) : null}
            </button>
          ))}
        </nav>
      )}

      {propertyId && (
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-manejai-bark/40"
              aria-hidden
            />
            <Input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Buscar por código, nome, lote, categoria…"
              aria-label="Buscar animais"
              className="pl-9"
            />
          </div>
          <div className="w-full sm:w-40 shrink-0">
            <Select
              value={sexFilter}
              onChange={(e) => setSexFilter(e.target.value)}
              options={[
                { value: '', label: 'Todos os sexos' },
                { value: 'female', label: 'Fêmeas' },
                { value: 'male', label: 'Machos' },
              ]}
            />
          </div>
        </div>
      )}

      {!propertyId ? (
        <Card><CardBody className="py-10 text-center text-manejai-bark/60">Selecione uma propriedade.</CardBody></Card>
      ) : isLoading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-16 rounded-xl bg-manejai-sand/40 animate-pulse" />)}</div>
      ) : showEmptyCadastro ? (
        <Card>
          <EmptyState
            icon={Tags}
            title="Nenhum animal"
            description="Cadastre animais individualmente ou use a importação em massa."
            action={
              <div className="flex flex-wrap justify-center gap-2">
                <Button onClick={() => setOpen(true)}>Cadastrar animal</Button>
                {showImport && (
                  <Button variant="outline" onClick={() => navigate('/importacao')}>
                    Importar CSV
                  </Button>
                )}
              </div>
            }
          />
        </Card>
      ) : showLotHistory && listTotal === 0 && !search && !sexFilter ? (
        <Card>
          <EmptyState
            icon={Tags}
            title="Histórico do lote vazio"
            description="Quando um animal sair deste lote (transferência, venda ou morte), a passagem aparece aqui com as datas de entrada e saída."
          />
        </Card>
      ) : inLotContext && listTab === 'active' && listTotal === 0 && !search && !sexFilter ? (
        <Card>
          <EmptyState
            icon={Tags}
            title="Nenhum animal neste lote"
            description="Transfira animais para este lote ou cadastre um novo animal já vinculado a ele."
          />
        </Card>
      ) : listTab === 'history' && !inLotContext && (data?.meta.total ?? 0) === 0 && !hasActiveFilters ? (
        <Card>
          <EmptyState
            icon={Tags}
            title="Histórico vazio"
            description="Quando uma venda for entregue ou abatida, o animal sai dos Ativos e aparece aqui."
          />
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-manejai-sand text-left text-xs">
                  <th className="px-5 py-3 font-medium w-14" aria-hidden />
                  {showLotHistory ? (
                    <>
                      <th className="px-5 py-3 font-medium">Código</th>
                      <th className="px-5 py-3 font-medium">Nome</th>
                      <th className="px-5 py-3 font-medium">Sexo</th>
                      <th className="px-5 py-3 font-medium">Categoria</th>
                      <th className="px-5 py-3 font-medium">Entrou</th>
                      <th className="px-5 py-3 font-medium">Saiu</th>
                      <th className="px-5 py-3 font-medium">Motivo da saída</th>
                    </>
                  ) : (
                    <>
                      <SortableTableHeader
                        label="Código"
                        column="internal_number"
                        activeColumn={sort.column}
                        direction={sort.dir}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        label="Nome"
                        column="animal_name"
                        activeColumn={sort.column}
                        direction={sort.dir}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        label="Sexo"
                        column="sex"
                        activeColumn={sort.column}
                        direction={sort.dir}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        label="Categoria"
                        column="category_name"
                        activeColumn={sort.column}
                        direction={sort.dir}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        label="Peso"
                        column="current_weight_kg"
                        activeColumn={sort.column}
                        direction={sort.dir}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        label="Nascimento"
                        column="birth_date"
                        activeColumn={sort.column}
                        direction={sort.dir}
                        onSort={handleSort}
                      />
                      {!inLotContext && (
                        <SortableTableHeader
                          label="Lote"
                          column="lot_name"
                          activeColumn={sort.column}
                          direction={sort.dir}
                          onSort={handleSort}
                        />
                      )}
                    </>
                  )}
                  <th className="px-3 py-3 font-medium whitespace-nowrap" aria-hidden />
                </tr>
              </thead>
              <tbody className="divide-y divide-manejai-sand/60">
                {showLotHistory ? (
                  lotHistoryRows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-5 py-12 text-center text-manejai-bark/60">
                        Nenhum animal encontrado
                        {search ? ` para “${search}”` : ''}.
                      </td>
                    </tr>
                  ) : (
                    lotHistoryRows.map((row) => (
                      <LotHistoryRow
                        key={`${row.id}-${row.entered_at ?? 'x'}-${row.exited_at}`}
                        row={row}
                        onOpen={() => openAnimalView(row)}
                        active={
                          menuOpenAnimalId === row.id || openViews.some((view) => view.id === row.id)
                        }
                      />
                    ))
                  )
                ) : (data?.animals.length ?? 0) === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-5 py-12 text-center text-manejai-bark/60">
                      Nenhum animal encontrado
                      {search ? ` para “${search}”` : ''}.
                    </td>
                  </tr>
                ) : (
                  data?.animals.map((a) => (
                  <ClickableTableRow
                    key={a.id}
                    onClick={() => openAnimalView(a)}
                    label={`Ver animal ${a.internal_number}`}
                    showViewIcon={false}
                    active={
                      menuOpenAnimalId === a.id || openViews.some((view) => view.id === a.id)
                    }
                    extraActions={
                      <AnimalRowActionsMenu
                        animal={a}
                        onOpenChange={(open) =>
                          setMenuOpenAnimalId(open ? a.id : null)
                        }
                        onCreateEvent={(animal) => {
                          setActionAnimal(animal);
                          setActiveModal('event');
                        }}
                        onCreateMovement={(animal) => {
                          setActionAnimal(animal);
                          setActiveModal('movement');
                        }}
                        onCreateReproduction={(animal) => {
                          setActionAnimal(animal);
                          setActiveModal('reproduction');
                        }}
                        onAssignLot={(animal) => {
                          setActionAnimal(animal);
                          setActiveModal('assignLot');
                        }}
                        onEditFicha={(animal) => navigate(`/animais/${animal.id}`)}
                        onDelete={setDeleteTarget}
                        canDelete={!!canDelete}
                      />
                    }
                  >
                    <td className="px-5 py-3">
                      <AnimalAvatar
                        coverPhotoUrl={a.cover_photo_url}
                        internalNumber={a.internal_number}
                        sex={a.sex}
                        size="sm"
                      />
                    </td>
                    <td className="px-5 py-3 font-medium text-manejai-forest">{a.internal_number}</td>
                    <td className="px-5 py-3 text-manejai-bark">{a.animal_name ?? '—'}</td>
                    <td className="px-5 py-3">
                      <Badge>
                        <span className="flex items-center gap-1.5">
                          <span>{sexLabel(a.sex)}</span>
                          <span className="text-base leading-none">{sexSymbol(a.sex)}</span>
                        </span>
                      </Badge>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <span>{a.category_name}</span>
                        {a.is_archive && (
                          <Badge tone="info" className="text-[10px] uppercase tracking-wide">
                            Arquivo
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3">{formatWeight(a.current_weight_kg)}</td>
                    <td className="px-5 py-3">{formatDate(a.birth_date)}</td>
                    {!inLotContext && (
                      <td
                        className="px-5 py-3"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        {a.lot_name && a.lot_id ? (
                          <Link
                            to={`/animais?lote=${a.lot_id}`}
                            className="font-medium text-manejai-moss hover:text-manejai-forest hover:underline"
                          >
                            {a.lot_name}
                          </Link>
                        ) : (
                          <span className="text-manejai-bark/40">Sem lote</span>
                        )}
                      </td>
                    )}
                  </ClickableTableRow>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!showLotHistory && (
            <ListPagination
              page={page}
              lastPage={data?.meta.last_page ?? 1}
              summary={`${data?.meta.total ?? 0} animal(is) no total`}
              onPageChange={setPage}
            />
          )}
          {showLotHistory && (
            <div className="border-t border-manejai-sand/60 px-5 py-3 text-sm text-manejai-bark/60">
              {listTotal} passagem(ns) no histórico
            </div>
          )}
        </Card>
      )}

      {propertyId && (
        <AnimalFormModal open={open} onClose={() => setOpen(false)} propertyId={propertyId} />
      )}

      <DeleteAnimalModal
        animal={deleteTarget}
        propertyAnimals={data?.animals}
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
      />

      {propertyId && actionAnimal && activeModal === 'event' && (
        <EventFormModal
          open
          onClose={closeActionModal}
          propertyId={propertyId}
          presetAnimal={actionAnimal}
        />
      )}
      {propertyId && actionAnimal && activeModal === 'movement' && (
        <MovementFormModal
          open
          onClose={closeActionModal}
          propertyId={propertyId}
          presetAnimalId={actionAnimal.id}
        />
      )}
      {propertyId && actionAnimal && activeModal === 'reproduction' && (
        <EventFormModal
          open
          onClose={closeActionModal}
          propertyId={propertyId}
          presetAnimal={actionAnimal}
          allowedTypes={REPRODUCTION_EVENT_TYPES}
          title="Registrar reprodução"
        />
      )}
      {propertyId && actionAnimal && activeModal === 'assignLot' && (
        <AssignLotModal
          open
          onClose={closeActionModal}
          propertyId={propertyId}
          animal={actionAnimal}
        />
      )}

      {openViews.map((animal, index) => (
        <AnimalFloatingView
          key={animal.id}
          animalId={animal.id}
          lotName={animal.lot_name}
          stackIndex={index}
          zIndex={focusedViewId === animal.id ? 60 : 50 + index}
          onClose={() => closeAnimalView(animal.id)}
          onFocus={() => setFocusedViewId(animal.id)}
        />
      ))}
    </div>
  );
}

function exitReasonLabel(reason: LotMembershipHistoryItem['exit_reason']): string {
  switch (reason) {
    case 'transfer':
      return 'Transferência';
    case 'sale':
      return 'Venda';
    case 'death':
      return 'Morte';
    case 'off_property':
      return 'Saída da fazenda';
    default:
      return '—';
  }
}

function LotHistoryRow({
  row,
  onOpen,
  active,
}: {
  row: LotMembershipHistoryItem;
  onOpen: () => void;
  active: boolean;
}) {
  return (
    <ClickableTableRow
      onClick={onOpen}
      label={`Ver animal ${row.internal_number}`}
      showViewIcon={false}
      active={active}
    >
      <td className="px-5 py-3">
        <AnimalAvatar
          coverPhotoUrl={row.cover_photo_url}
          internalNumber={row.internal_number}
          sex={row.sex}
          size="sm"
        />
      </td>
      <td className="px-5 py-3 font-medium text-manejai-forest">{row.internal_number}</td>
      <td className="px-5 py-3 text-manejai-bark">{row.animal_name ?? '—'}</td>
      <td className="px-5 py-3">
        <Badge>
          <span className="flex items-center gap-1.5">
            <span>{sexLabel(row.sex)}</span>
            <span className="text-base leading-none">{sexSymbol(row.sex)}</span>
          </span>
        </Badge>
      </td>
      <td className="px-5 py-3">{row.category_name}</td>
      <td className="px-5 py-3">{formatDate(row.entered_at)}</td>
      <td className="px-5 py-3">{formatDate(row.exited_at)}</td>
      <td className="px-5 py-3 text-manejai-bark/80">{exitReasonLabel(row.exit_reason)}</td>
    </ClickableTableRow>
  );
}

function AnimalFormModal({
  open,
  onClose,
  propertyId,
}: {
  open: boolean;
  onClose: () => void;
  propertyId: number;
}) {
  const createAnimal = useCreateAnimal();
  const { data: lots } = useLots(propertyId);
  const { data: breeds } = useBreeds();
  const { data: categories } = useCategories();
  const [error, setError] = useState('');
  const [sex, setSex] = useState<'male' | 'female'>('female');
  const [form, setForm] = useState({
    internal_number: '',
    name: '',
    lot_id: '',
    category_code: '',
    breed_id: '',
    birth_date: '',
    current_weight_kg: '',
    is_archive: false,
  });

  const filteredCategories = useMemo(
    () => categories?.filter((c) => c.sex === sex) ?? [],
    [categories, sex],
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      await createAnimal.mutateAsync({
        property_id: propertyId,
        internal_number: form.internal_number,
        name: form.name || null,
        sex,
        lot_id: form.lot_id ? Number(form.lot_id) : null,
        category_code: form.category_code,
        breed_id: form.breed_id ? Number(form.breed_id) : null,
        birth_date: form.birth_date || null,
        current_weight_kg: form.current_weight_kg ? Number(form.current_weight_kg) : null,
        is_archive: form.is_archive,
      });
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao salvar.'));
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Novo animal"
      size="lg"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" form="create-animal-form-modal" loading={createAnimal.isPending}>
            Salvar
          </Button>
        </>
      }
    >
      <form id="create-animal-form-modal" onSubmit={handleSubmit} className="space-y-4">
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Código" required value={form.internal_number} onChange={(e) => setForm({ ...form, internal_number: e.target.value })} />
          <Input label="Nome/apelido" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <Select
          label="Sexo"
          value={sex}
          onChange={(e) => setSex(e.target.value as 'male' | 'female')}
          options={[
            { value: 'female', label: 'Fêmea' },
            { value: 'male', label: 'Macho' },
          ]}
        />
        <div className="grid sm:grid-cols-2 gap-4">
          <Select
            label="Categoria"
            required
            value={form.category_code}
            onChange={(e) => setForm({ ...form, category_code: e.target.value })}
            placeholder="Selecione…"
            options={filteredCategories.map((c) => ({ value: c.code, label: c.name }))}
          />
          <Select
            label="Lote"
            value={form.lot_id}
            onChange={(e) => setForm({ ...form, lot_id: e.target.value })}
            placeholder="Sem lote"
            allowEmpty
            options={(lots?.lots ?? []).map((l) => ({ value: l.id, label: l.name }))}
          />
        </div>
        <Select
          label="Raça"
          value={form.breed_id}
          onChange={(e) => setForm({ ...form, breed_id: e.target.value })}
          placeholder="Opcional"
          options={(breeds ?? []).map((b) => ({
            value: b.id,
            label: breedLabel({ abbreviation: b.abbreviation, name: b.name }),
          }))}
        />
        <div className="grid sm:grid-cols-2 gap-4">
          <DateInput label="Data nascimento" value={form.birth_date} onChange={(birth_date) => setForm({ ...form, birth_date })} />
          <Input label="Peso atual (kg)" type="number" step="0.1" value={form.current_weight_kg} onChange={(e) => setForm({ ...form, current_weight_kg: e.target.value })} />
        </div>
        <label className="flex items-start gap-2 rounded-xl border border-manejai-sand/80 bg-manejai-cream/30 px-3 py-2.5 text-sm text-manejai-bark">
          <input
            type="checkbox"
            checked={form.is_archive}
            onChange={(e) => setForm({ ...form, is_archive: e.target.checked })}
            className="mt-0.5 rounded border-manejai-sand text-manejai-forest focus:ring-manejai-moss/30"
          />
          <span>
            <span className="font-medium text-manejai-forest">Arquivo (apenas registro genealógico)</span>
            <span className="mt-0.5 block text-xs text-manejai-bark/65">
              Animais de arquivo não aparecem em listas operacionais nem em eventos e movimentações.
            </span>
          </span>
        </label>
      </form>
    </Modal>
  );
}
